import React, { useState, useMemo } from 'react';
import useAnalytics from '../../hooks/useAnalytics';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Chip,
  Grid,
  Divider,
  Alert,
  Paper,

  IconButton,
  Avatar,
  alpha,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
  Snackbar,
} from '@mui/material';
import {
  Close as CloseIcon,
  GetApp as GetAppIcon,
  ExpandMore as ExpandMoreIcon,
  FileDownload as FileDownloadIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

// ExportContent component that can be embedded in tabs or used in dialog
export const ExportContent = ({
  categories = [],
  hotels = [],
  filteredHotels = [],
  filters = {},
  onExport,
  onCancel,
  isExporting = false,
}) => {
  const [selectedCategories, setSelectedCategories] = useState({});
  const [alertMessage, setAlertMessage] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [useFiltered, setUseFiltered] = useState(Object.keys(filters).length > 0);
  
  // Get the actual list of hotels to export
  const hotelsToExport = useFiltered ? filteredHotels : hotels;

  // Helper function to group categories by their group
  const groupedCategories = useMemo(() => {
    const groups = {};
    
    categories.forEach(group => {
      groups[group.id] = {
        title: group.title,
        id: group.id,
        items: group.items || []
      };
    });
    
    return groups;
  }, [categories]);

  // Handle category selection/deselection
  const handleCategoryChange = (groupId, fieldName) => {
    setSelectedCategories(prev => {
      const groupCategories = prev[groupId] || [];
      const newGroupCategories = groupCategories.includes(fieldName)
        ? groupCategories.filter(name => name !== fieldName)
        : [...groupCategories, fieldName];
        
      return {
        ...prev,
        [groupId]: newGroupCategories
      };
    });
  };

  // Select all categories in a group
  const handleSelectAll = (groupId) => {
    setSelectedCategories(prev => {
      const group = groupedCategories[groupId];
      if (!group) return prev;
      
      const allFieldNames = group.items.map(item => item.fieldName);
      const currentGroupCategories = prev[groupId] || [];
      
      // If all are selected, deselect all. Otherwise, select all.
      const newGroupCategories = currentGroupCategories.length === allFieldNames.length ? [] : allFieldNames;
      
      return {
        ...prev,
        [groupId]: newGroupCategories
      };
    });
  };

  // Count selected categories for each group
  const getGroupSelectionCount = (groupId) => {
    const group = groupedCategories[groupId];
    if (!group) return { selected: 0, total: 0 };
    
    const total = group.items.length;
    const selected = (selectedCategories[groupId] || []).length;
    
    return { selected, total };
  };

  // Prepare export data (common for both CSV and XLSX)
  const prepareExportData = () => {
    // Build a list of field headers and data extractors
    const fields = [];
    
    // Always include basic fields
    fields.push({ 
      header: 'Hotel Name', 
      extract: (hotel) => {
        // First try to get name from categoryData if it exists there
        const nameFromCategory = hotel.categoryData?.category_name?.value || '';
        // Fall back to direct hotel.name property if the above doesn't exist
        return nameFromCategory || hotel.name || '';
      }
    });
    
    fields.push({ 
      header: 'ID', 
      extract: (hotel) => hotel.id || '' 
    });
    
    // Add selected category fields
    Object.entries(selectedCategories).forEach(([groupId, fieldNames]) => {
      fieldNames.forEach(fieldName => {
        const group = groupedCategories[groupId];
        const category = group?.items.find(item => item.fieldName === fieldName);
        
        if (category) {
          fields.push({
            header: category.title,
            extract: (hotel) => {
              const categoryKey = `category_${fieldName.toLowerCase()}`;
              return hotel.categoryData?.[categoryKey]?.value || '';
            }
          });
        }
      });
    });
    
    // If no fields selected beyond ID and name, show error
    if (fields.length <= 2) {
      setAlertMessage('Please select at least one category to export');
      return null;
    }
    
    // Extract headers and rows
    const headers = fields.map(field => field.header);
    
    // Create data rows for each hotel
    const rows = hotelsToExport.map(hotel => {
      return fields.map(field => field.extract(hotel));
    });
    
    // Generate filename with date
    const date = new Date().toISOString().split('T')[0];
    const filtered = Object.keys(filters).length > 0 ? '_filtered' : '';
    const extension = exportFormat === 'csv' ? 'csv' : 'xlsx';
    const filename = `hotels_export_${date}${filtered}.${extension}`;
    
    return { 
      headers,
      rows,
      filename,
      count: hotelsToExport.length 
    };
  };

  // Convert data to CSV format
  const convertToCSV = (headers, rows) => {
    // Create header row
    const headerRow = headers.map(header => `"${header}"`).join(',');
    
    // Create data rows
    const dataRows = rows.map(row => {
      return row.map(value => {
        // Escape any double quotes and wrap in quotes to handle commas
        return `"${value?.toString().replace(/"/g, '""') || ''}"`;
      }).join(',');
    }).join('\n');
    
    // Combine headers and data
    return `${headerRow}\n${dataRows}`;
  };

  // Handle export button click
  const handleExportClick = () => {
    const exportData = prepareExportData();
    if (exportData) {
      onExport({
        ...exportData,
        content: exportFormat === 'csv' ? convertToCSV(exportData.headers, exportData.rows) : { headers: exportData.headers, rows: exportData.rows },
        format: exportFormat
      });
    }
  };

  // Handle format change
  const handleFormatChange = (event) => {
    setExportFormat(event.target.value);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Toggle between filtered and all hotels
  const handleFilterToggle = (useFiltered) => {
    setUseFiltered(useFiltered);
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column'
    }}>
      <Box sx={{ 
        p: 3,
        flexGrow: 1,
        overflow: 'auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {alertMessage && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            onClose={() => setAlertMessage('')}
          >
            {alertMessage}
          </Alert>
        )}
        
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mb: 4, 
            borderRadius: 4,
            bgcolor: 'background.paper',
            boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <InfoIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              fontFamily: 'StrawberrySansDisplay',
              color: 'text.primary',
              fontSize: '1.25rem'
            }}>
              Export Information
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{
            fontFamily: 'StrawberrySansText',
            fontSize: '0.875rem',
            lineHeight: 1.6
          }}>
            You are exporting <strong>{hotelsToExport.length}</strong> hotels
            {Object.keys(filters).length > 0 && ' with filters applied'}.
            Select the categories below to include in your export.
          </Typography>
        </Paper>

        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{ 
            mb: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              bgcolor: 'primary.main'
            }
          }}
        >
          <Tab 
            label="Select Categories" 
            sx={{ 
              textTransform: 'none',
              fontFamily: 'StrawberrySansText',
              fontWeight: 500,
              fontSize: '0.95rem',
              minHeight: 56,
              px: 3,
              borderRadius: '12px 12px 0 0',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600
              },
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                borderRadius: '8px 8px 0 0'
              }
            }} 
          />
          <Tab 
            label="Format Options" 
            sx={{ 
              textTransform: 'none',
              fontFamily: 'StrawberrySansText',
              fontWeight: 500,
              fontSize: '0.95rem',
              minHeight: 56,
              px: 3,
              borderRadius: '12px 12px 0 0',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600
              },
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                borderRadius: '8px 8px 0 0'
              }
            }} 
          />
          <Tab 
            label="Preview" 
            sx={{ 
              textTransform: 'none',
              fontFamily: 'StrawberrySansText',
              fontWeight: 500,
              fontSize: '0.95rem',
              minHeight: 56,
              px: 3,
              borderRadius: '12px 12px 0 0',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600
              },
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                borderRadius: '8px 8px 0 0'
              }
            }} 
          />
        </Tabs>

        {activeTab === 0 && (
          <Box>
            {categories.map(group => {
              const { selected, total } = getGroupSelectionCount(group.id);
              
              return (
                <Accordion 
                  key={group.id} 
                  sx={{ 
                    mb: 2,
                    borderRadius: 3,
                    boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                    border: 'none',
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      mb: 3,
                      boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      bgcolor: 'background.paper',
                      borderRadius: 3,
                      '&.Mui-expanded': {
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      }
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      width: '100%',
                      pr: 2
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <GetAppIcon sx={{ fontSize: 16 }} />
                        </Box>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 500,
                            fontFamily: 'StrawberrySansDisplay',
                            color: 'text.primary',
                            fontSize: '1.1rem'
                          }}
                        >
                          {group.title}
                        </Typography>
                        <Chip 
                          label={`${selected}/${total}`}
                          size="small"
                          color={selected > 0 ? "primary" : "default"}
                          sx={{ 
                            height: 20,
                            '& .MuiChip-label': { px: 1, fontSize: '0.75rem' }
                          }}
                        />
                      </Box>
                      <Button 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectAll(group.id);
                        }}
                        sx={{ 
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          borderRadius: 2,
                          px: 2,
                          '&:hover': {
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                          }
                        }}
                      >
                        {selected === total ? 'Deselect All' : 'Select All'}
                      </Button>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ 
                    px: 3, 
                    pt: 2, 
                    pb: 3,
                    bgcolor: 'background.paper',
                    borderBottomLeftRadius: 3,
                    borderBottomRightRadius: 3
                  }}>
                    <Grid container spacing={2}>
                      {group.items?.map(item => (
                        <Grid item xs={12} sm={6} md={4} key={item.fieldName}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={(selectedCategories[group.id] || []).includes(item.fieldName)}
                                onChange={() => handleCategoryChange(group.id, item.fieldName)}
                                size="small"
                                sx={{
                                  '&.Mui-checked': {
                                    color: 'primary.main',
                                  }
                                }}
                              />
                            }
                            label={item.title}
                            sx={{
                              '& .MuiFormControlLabel-label': {
                                fontSize: '0.875rem',
                                fontFamily: 'StrawberrySansText',
                                fontWeight: 400
                              }
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ p: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5,
              mb: 3
            }}>
              <Box sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <GetAppIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 500,
                  fontFamily: 'StrawberrySansDisplay',
                  color: 'text.primary',
                  fontSize: '1.25rem'
                }}
              >
                Export Format
              </Typography>
            </Box>
            
            <Paper 
              elevation={0}
              sx={{
                p: 4,
                mb: 3,
                bgcolor: 'background.paper',
                borderRadius: 4,
                boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`
                }
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500 }}>Export Options</Typography>
              
              <Grid container spacing={2}>
                {/* Format Selection */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Format</Typography>
                  <RadioGroup
                    row
                    value={exportFormat}
                    onChange={handleFormatChange}
                  >
                    <FormControlLabel 
                      value="xlsx" 
                      control={<Radio size="small" />} 
                      label="Excel (.xlsx)" 
                    />
                    <FormControlLabel 
                      value="csv" 
                      control={<Radio size="small" />} 
                      label="CSV (.csv)" 
                    />
                  </RadioGroup>
                </Grid>
                
                {/* Filtered/All Toggle - Only show if filters are applied */}
                {Object.keys(filters).length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>Hotels to Export</Typography>
                    <RadioGroup
                      row
                      value={useFiltered ? "filtered" : "all"}
                      onChange={(e) => handleFilterToggle(e.target.value === "filtered")}
                    >
                      <FormControlLabel 
                        value="filtered" 
                        control={<Radio size="small" />} 
                        label={`Filtered (${filteredHotels.length})`} 
                      />
                      <FormControlLabel 
                        value="all" 
                        control={<Radio size="small" />} 
                        label={`All (${hotels.length})`} 
                      />
                    </RadioGroup>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Box>
        )}

        {activeTab === 2 && (
          <Box>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                mb: 2, 
                fontWeight: 400,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <InfoIcon sx={{ fontSize: 18 }} />
              Preview of fields to be exported:
            </Typography>
            
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                borderRadius: 2,
                mb: 2
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1, 
                mb: 2 
              }}>
                <Chip 
                  label="Hotel Name" 
                  size="small" 
                  color="primary" 
                  sx={{ fontWeight: 500 }}
                />
                <Chip 
                  label="ID" 
                  size="small" 
                  color="primary" 
                  sx={{ fontWeight: 500 }}
                />
                
                {Object.entries(selectedCategories).map(([groupId, fieldNames]) =>
                  fieldNames.map(fieldName => {
                    const group = groupedCategories[groupId];
                    const category = group?.items.find(item => item.fieldName === fieldName);
                    
                    return category ? (
                      <Chip 
                        key={fieldName}
                        label={category.title}
                        size="small"
                        sx={{ fontWeight: 400 }}
                      />
                    ) : null;
                  })
                )}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Will export <strong>{hotelsToExport.length}</strong> hotels with <strong>{
                    2 + Object.values(selectedCategories).reduce((sum, arr) => sum + arr.length, 0)
                  }</strong> columns as <strong>{exportFormat === 'csv' ? 'CSV' : 'Excel'}</strong> file.
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>
      
      <Box sx={{ 
        p: 4, 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Button 
          variant="outlined" 
          onClick={onCancel}
          disabled={isExporting}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            fontWeight: 500,
            fontFamily: 'StrawberrySansText',
            textTransform: 'none',
            '&:hover': {
              bgcolor: theme => alpha(theme.palette.error.main, 0.08),
              color: 'error.main',
              borderColor: 'error.main'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExportClick}
          disabled={isExporting || Object.values(selectedCategories).flat().length === 0}
          endIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            fontWeight: 500,
            fontFamily: 'StrawberrySansText',
            textTransform: 'none',
            boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: theme => `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          Export as {exportFormat === 'csv' ? 'CSV' : 'Excel'}
        </Button>
      </Box>
    </Box>
  );
};

// Main ExportDialog component (wrapper) for standalone use
const ExportDialog = ({
  open,
  onClose,
  categories = [],
  hotels = [],
  filteredHotels = hotels,
  filters = {},
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { trackExport, trackUserAction } = useAnalytics();

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle export action
  const handleExport = async (exportData) => {
    try {
      setIsExporting(true);
      
      // Track export analytics - KEY BUSINESS METRIC
      trackExport(exportData.format, exportData.headers?.length || 0, {
        hotel_count: hotels.length,
        export_filename: exportData.filename,
        is_filtered_export: Array.isArray(filteredHotels) && filteredHotels.length !== hotels.length,
        active_filters: Object.keys(filters || {}).length,
        file_size_estimate: exportData.content?.length || 0
      });
      
      trackUserAction('data_exported', `${exportData.format}_export`, {
        export_type: exportData.format,
        record_count: Array.isArray(filteredHotels) ? filteredHotels.length : hotels.length
      });
      
      if (exportData.format === 'csv') {
        // Create and download CSV file
        const blob = new Blob([exportData.content], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', exportData.filename);
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // For Excel format, we need to dynamically load the xlsx library
        try {
          // Use dynamic import to load the xlsx library only when needed
          const XLSX = await import('https://cdn.jsdelivr.net/npm/xlsx@0.18.5/+esm');
          
          // Create workbook
          const wb = XLSX.utils.book_new();
          
          // Create worksheet data - array of objects with proper key-value pairs
          const wsData = [];
          const { headers, rows } = exportData;
          
          // Convert rows to an array of objects with header keys
          for (let i = 0; i < rows.length; i++) {
            const rowObj = {};
            for (let j = 0; j < headers.length; j++) {
              // Use header as the key, row data as the value
              rowObj[headers[j]] = rows[i][j];
            }
            wsData.push(rowObj);
          }
          
          // Create worksheet using json_to_sheet for proper column structure
          const ws = XLSX.utils.json_to_sheet(wsData);
          
          // Set column widths for better readability
          const colWidths = headers.map(header => ({ wch: Math.max(header.length * 1.5, 10) }));
          ws['!cols'] = colWidths;
          
          // Add worksheet to workbook
          XLSX.utils.book_append_sheet(wb, ws, "Hotels");
          
          // Generate Excel file and trigger download
          XLSX.writeFile(wb, exportData.filename);
        } catch (error) {
          console.error("Error loading XLSX library or creating Excel file:", error);
          // Fallback to CSV if Excel export fails
          alert("Excel export failed. Please try CSV format instead or check console for details.");
          
          // Create and download CSV file as fallback
          const blob = new Blob([convertToCSV(exportData.headers, exportData.rows)], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.setAttribute('href', url);
          link.setAttribute('download', exportData.filename.replace('.xlsx', '.csv'));
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
      
      setSnackbar({
        open: true,
        message: `Successfully exported ${exportData.count} hotels`,
        severity: 'success'
      });
      
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      setSnackbar({
        open: true,
        message: `Export failed: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Helper function to convert data to CSV (for fallback)
  const convertToCSV = (headers, rows) => {
    // Create header row
    const headerRow = headers.map(header => `"${header}"`).join(',');
    
    // Create data rows
    const dataRows = rows.map(row => {
      return row.map(value => {
        // Escape any double quotes and wrap in quotes to handle commas
        return `"${value?.toString().replace(/"/g, '""') || ''}"`;
      }).join(',');
    }).join('\n');
    
    // Combine headers and data
    return `${headerRow}\n${dataRows}`;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2 },
            minHeight: { xs: '100%', sm: 'auto' },
            maxHeight: { xs: '100%', sm: '80vh' }
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: { xs: 2, sm: 3 },
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.7),
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(8px)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              width: 40,
              height: 40,
              boxShadow: (theme) => `0 0 0 1px ${alpha(theme.palette.primary.main, 0.05)}, 0 2px 5px ${alpha(theme.palette.common.black, 0.05)}`,
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15),
              }
            }}>
              <GetAppIcon />
            </Avatar>
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 400,
                  mb: 0.5,
                  fontFamily: 'StrawberrySansDisplay',
                  letterSpacing: '-0.02em'
                }}
              >
                Export Hotel Data
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  opacity: 0.8,
                  fontFamily: 'StrawberrySansText'
                }}
              >
                {filteredHotels.length > 0 
                  ? `Exporting ${filteredHotels.length} filtered hotels` 
                  : `Exporting all ${hotels.length} hotels`}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={onClose}
            sx={{ 
              color: 'text.secondary',
              '&:hover': {
                color: 'error.main',
                bgcolor: (theme) => alpha(theme.palette.error.light, 0.1)
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <ExportContent
            categories={categories}
            filters={filters}
            filteredHotels={filteredHotels}
            hotels={hotels}
            onExport={handleExport}
            onCancel={onClose}
            isExporting={isExporting}
          />
        </DialogContent>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            alignItems: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            '& .MuiAlert-icon': {
              fontSize: '1.25rem'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExportDialog; 